import { useId } from 'react';
import { ListFilter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import type { IncidentPosition } from 'hooks/incident/useRoster';
import type { RosterOccupancyOption } from '../RosterOrgChart';
import {
  SearchableSelect,
  type SearchableSelectOption,
} from './SearchableSelect';
import {
  hasRosterNarrowingFilters,
  rosterFilterCategoryOptions,
  rosterFilterPositionOptions,
  ROSTER_OCCUPANCY_OPTIONS,
  toggleRosterFilterValue,
  type RosterFilters,
} from './rosterPositionFilter';

type RosterFilterMenuProps = {
  filters: RosterFilters;
  onFiltersChange: (next: RosterFilters) => void;
  positions: IncidentPosition[];
  hideDraftOccupancy?: boolean;
  /** Icon button only — used on the live incident and Hub template toolbars. */
  iconOnly?: boolean;
};

export function RosterFilterMenu({
  filters,
  onFiltersChange,
  positions,
  hideDraftOccupancy = false,
  iconOnly = false,
}: RosterFilterMenuProps) {
  const idPrefix = useId();
  const filtersActive = hasRosterNarrowingFilters(filters);
  const positionOptions = rosterFilterPositionOptions(positions).map(
    (option) => ({
      value: option.value,
      label: option.label,
    })
  );
  const selectedPosition =
    positionOptions.find((option) => option.value === filters.positionIds[0]) ??
    null;
  const categoryOptions = rosterFilterCategoryOptions(positions);
  const occupancyOptions = hideDraftOccupancy
    ? ROSTER_OCCUPANCY_OPTIONS.filter((option) => option.value !== 'draft')
    : ROSTER_OCCUPANCY_OPTIONS;

  return (
    <div data-tour="roster-filters">
      <Popover>
        <PopoverTrigger
          render={
            <Button
              type="button"
              variant="outline"
              size={iconOnly ? 'icon-sm' : 'sm'}
              aria-label="Filters"
              title="Filters"
              className="tw:relative"
            />
          }
        >
          <ListFilter className="tw:h-4 tw:w-4" />
          {iconOnly ? null : 'Filters'}
          {filtersActive ? (
            <span
              className="tw:absolute tw:right-1 tw:top-1 tw:size-1.5 tw:rounded-full tw:bg-primary"
              aria-hidden
            />
          ) : null}
        </PopoverTrigger>
        <PopoverContent
          align="end"
          className="dark tw:max-h-[min(36rem,80vh)] tw:w-96 tw:overflow-y-auto tw:p-3"
        >
          <div className="tw:flex tw:flex-col tw:gap-3">
          <div className="tw:flex tw:flex-col tw:gap-2">
            <div className="tw:text-xs tw:font-semibold tw:tracking-wide tw:text-muted-foreground tw:uppercase">
              Visibility
            </div>
            {occupancyOptions.map((option) => {
              const optionId = `${idPrefix}-occupancy-${option.value}`;
              return (
                <div
                  key={option.value}
                  className="tw:flex tw:items-center tw:gap-2"
                >
                  <Checkbox
                    id={optionId}
                    checked={filters.occupancy.includes(option.value)}
                    onCheckedChange={() => {
                      const current = filters.occupancy;
                      const next = current.includes(option.value)
                        ? current.filter((value) => value !== option.value)
                        : [...current, option.value];
                      if (next.length === 0) return;
                      onFiltersChange({
                        ...filters,
                        occupancy: next as RosterOccupancyOption[],
                      });
                    }}
                  />
                  <Label htmlFor={optionId} className="tw:font-normal">
                    {option.label}
                  </Label>
                </div>
              );
            })}
          </div>

          <SearchableSelect
            id={`${idPrefix}-subtree`}
            label="Only show this position and its subordinates"
            options={positionOptions}
            value={selectedPosition}
            onChange={(next: SearchableSelectOption | null) =>
              onFiltersChange({
                ...filters,
                positionIds: next ? [next.value] : [],
              })
            }
            placeholder="Select a position"
            clearable
            contentClassName="tw:z-[60]"
            positionerClassName="tw:z-[60]"
          />

          <div className="tw:flex tw:flex-col tw:gap-2">
            <div className="tw:text-xs tw:font-semibold tw:tracking-wide tw:text-muted-foreground tw:uppercase">
              Category
            </div>
            {categoryOptions.length === 0 ? (
              <div className="tw:text-xs tw:text-muted-foreground">
                No values
              </div>
            ) : (
              categoryOptions.map((option) => {
                const optionId = `${idPrefix}-category-${option.value}`;
                return (
                  <div
                    key={option.value}
                    className="tw:flex tw:items-center tw:gap-2"
                  >
                    <Checkbox
                      id={optionId}
                      checked={filters.categories.includes(option.value)}
                      onCheckedChange={() =>
                        onFiltersChange({
                          ...filters,
                          categories: toggleRosterFilterValue(
                            filters.categories,
                            option.value
                          ),
                        })
                      }
                    />
                    <Label htmlFor={optionId} className="tw:font-normal">
                      {option.label}
                    </Label>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </PopoverContent>
      </Popover>
    </div>
  );
}
